import React, { FC, useEffect, useState } from "react";
import { Card, CardBody, CardHeader } from "../../../partials/controls";
import { api } from "../../../common/api";
import { toast } from "react-toastify";

interface Permission {
  [key: string]: string;
}

interface PermissionConfig {
  label: string;
  key: string;
  hasNA?: boolean;
}

const StaffPermission: FC = () => {
  const [permissions, setPermissions] = useState<Permission>({});
  const [loading, setLoading] = useState(false);

  const permissionConfigs: PermissionConfig[] = [
    { label: "Regular Schedule", key: "regularSchedule" },
    { label: "Today Schedule", key: "todaySchedule" },
    { label: "Pre-schedule", key: "preSchedule" },
    { label: "Driver Profile", key: "driverProfile" },
    { label: "Car Profile", key: "carProfile" },
    { label: "Push Notification", key: "pushNotification" },
    { label: "Report", key: "report" },
    { label: "Excel Report", key: "excelReport", hasNA: true },
  ];

  const getPermissions = async () => {
    setLoading(true);
    try {
      const { data }: any = await api({
        url: `/role-permission/STAFF`,
        method: "get",
      });
      if (data.serverResponse.isError) {
        // If doesn't exist, create default permissions
        await createDefaultPermissions();
      } else {
        setPermissions(data.result.data?.permissions || {});
      }
    } catch (err) {
      // If error, try to create default permissions
      await createDefaultPermissions();
    } finally {
      setLoading(false);
    }
  };

  const createDefaultPermissions = async () => {
    const defaultPermissions: Permission = {
      regularSchedule: "NONE",
      todaySchedule: "NONE",
      preSchedule: "NONE",
      driverProfile: "NONE",
      carProfile: "NONE",
      pushNotification: "NONE",
      report: "NONE",
      excelReport: "NONE",
    };

    try {
      const { data }: any = await api({
        url: `/role-permission`,
        method: "post",
        body: {
          roleType: "STAFF",
          permissions: defaultPermissions,
        },
      });
      if (!data.serverResponse.isError) {
        setPermissions(defaultPermissions);
        toast.success("Default permissions created");
      }
    } catch (err) {
      // @ts-ignore
      toast.error("Failed to create permissions");
    }
  };

  const handlePermissionChange = async (key: string, value: string) => {
    const newPermissions = { ...permissions, [key]: value };
    setPermissions(newPermissions);

    try {
      const { data }: any = await api({
        url: `/role-permission/STAFF`,
        method: "put",
        body: {
          permissions: newPermissions,
        },
      });

      if (data.serverResponse.isError) {
        toast.error(data.serverResponse.message);
        // Revert on error
        setPermissions(permissions);
      } else {
        toast.success("Permission updated successfully");
      }
    } catch (err) {
      // @ts-ignore
      toast.error("Failed to update permission");
      // Revert on error
      setPermissions(permissions);
    }
  };

  useEffect(() => {
    getPermissions();
  }, []);

  return (
    <Card>
      <CardHeader title="Staff Permissions"></CardHeader>
      <CardBody>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="row">
            {permissionConfigs.map((config) => (
              <div
                key={config.key}
                className="col-lg-6 col-md-6 col-sm-12 mb-4"
              >
                <div className="row">
                  <div className="col-lg-4 ws-nowrap">{config.label}</div>
                  <div className="col-lg-8">
                    <div className="d-flex align-items-center flex-wrap">
                      <label className="radio radio-outline radio-success mr-5">
                        <input
                          type="radio"
                          name={config.key}
                          checked={permissions[config.key] === "NONE"}
                          onChange={() =>
                            handlePermissionChange(config.key, "NONE")
                          }
                        />
                        <span></span>
                        Forbidden
                      </label>
                      <label className="radio radio-outline radio-success mr-5">
                        <input
                          type="radio"
                          name={config.key}
                          checked={permissions[config.key] === "READ_ONLY"}
                          onChange={() =>
                            handlePermissionChange(config.key, "READ_ONLY")
                          }
                        />
                        <span></span>
                        Read-only
                      </label>
                      {!config.hasNA && (
                        <label className="radio radio-outline radio-success">
                          <input
                            type="radio"
                            name={config.key}
                            checked={permissions[config.key] === "EDITABLE"}
                            onChange={() =>
                              handlePermissionChange(config.key, "EDITABLE")
                            }
                          />
                          <span></span>
                          Editable
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default StaffPermission;
